import { useMemo } from "react"
import { useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { useFormatters } from "@/workspace/values"
import type { Finding, Reason } from "./match"

/** One hue per verdict: settled green, ready-to-post lineage blue, findings warning to destructive. */
export const PAY_REASON_COLOR: Record<Reason, string> = {
  posted: "var(--success)",
  refund_booked: "var(--success)",
  deposited: "var(--success)",
  to_post: "var(--lineage)",
  deposit_by_amount: "var(--lineage)",
  fee_mismatch: "var(--warning)",
  already_paid: "var(--warning)",
  deposit_mismatch: "var(--warning)",
  chargeback: "color-mix(in oklab, var(--warning) 55%, var(--destructive))",
  duplicate_capture: "color-mix(in oklab, var(--warning) 55%, var(--destructive))",
  refund_no_credit_note: "color-mix(in oklab, var(--warning) 55%, var(--destructive))",
  batch_not_sum: "var(--destructive)",
  missing_payout: "var(--destructive)",
  no_invoice: "var(--destructive)",
}

export function PayReasonPill({ reason, className }: { reason: Reason; className?: string }) {
  const t = useT()
  return (
    <span className={cn("inline-flex h-5 items-center gap-1.5 rounded-full border border-border px-2 text-2xs whitespace-nowrap", className)}>
      <span className="size-1.5 rounded-full" style={{ background: PAY_REASON_COLOR[reason] }} aria-hidden />
      {t(`pw.reason.${reason}` as const)}
    </span>
  )
}

/** EGP with two decimals in the reader's locale; an em dash for a value that is hidden or unreadable. */
export function useMoney(): (value: number | null) => string {
  const { tag } = useFormatters()
  return useMemo(() => {
    const format = new Intl.NumberFormat(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return (value) => (value === null ? "—" : format.format(value))
  }, [tag])
}

/** Each finding in plain words: what the rule saw, and what it means for the books. */
export function useFindingWhy(): (f: Finding) => string {
  const t = useT()
  const money = useMoney()
  return (f) => {
    if (f.subject === "batch") {
      const b = f.batch
      const ref = b.bankReference ?? ""
      switch (f.reason) {
        case "deposited":
          return t("pw.why.deposited", { batch: b.id, provider: b.provider, ref: f.deposit?.reference ?? ref, date: f.deposit?.date ?? "" })
        case "deposit_by_amount":
          return t("pw.why.depositByAmount", { batch: b.id, ref: f.deposit?.reference ?? "", expected: ref })
        case "batch_not_sum":
          return t("pw.why.batchNotSum", { batch: b.id, provider: b.provider, reported: money(b.reportedNet), lines: money(f.lineNet), delta: money(f.delta) })
        case "missing_payout":
          return t("pw.why.missingPayout", { batch: b.id, provider: b.provider, date: b.date ?? "", ref })
        case "deposit_mismatch":
          return t("pw.why.depositMismatch", { batch: b.id, ref, delta: money(f.delta) })
      }
    }
    const p = f.payment
    const order = f.store?.name ?? p.orderRef
    const erp = f.erp ?? ""
    const amount = money(Math.abs(p.gross ?? 0))
    switch (f.reason) {
      case "posted":
        return f.confidence < 90 ? t("pw.why.postedCheck", { provider: p.provider, id: p.id, erp }) : t("pw.why.posted", { provider: p.provider, id: p.id, erp })
      case "to_post":
        return f.delta && f.confidence < 90 ? t("pw.why.toPostDelta", { id: p.id, order, erp, delta: money(f.delta) }) : t("pw.why.toPost", { id: p.id, order, erp })
      case "refund_booked":
        return t("pw.why.refundBooked", { id: p.id, amount, erp })
      case "fee_mismatch":
        return t("pw.why.feeMismatch", { id: p.id, provider: p.provider, charged: money((p.fee ?? 0) + (p.vat ?? 0)), expected: money(f.expected ? f.expected.fee + f.expected.vat : null), delta: money(f.delta) })
      case "duplicate_capture":
        return t("pw.why.duplicateCapture", { id: p.id, first: f.duplicateOf ?? "", order, amount })
      case "no_invoice":
        return !f.store ? t("pw.why.noStoreOrder", { id: p.id, order }) : f.store.reference ? t("pw.why.noInvoice", { id: p.id, order }) : t("pw.why.noReference", { id: p.id, order })
      case "already_paid":
        return t("pw.why.alreadyPaid", { id: p.id, erp })
      case "refund_no_credit_note":
        return t("pw.why.refundNoCreditNote", { id: p.id, amount, order, erp })
      case "chargeback":
        return t("pw.why.chargeback", { id: p.id, amount, order })
    }
    return ""
  }
}

/** A finding's queue line. */
export function findingLabel(f: Finding): string {
  return f.subject === "batch" ? `${f.batch.provider} · ${f.batch.id}` : `${f.payment.provider} · ${f.payment.id}${f.store ? ` · ${f.store.name}` : ""}`
}
